import { paths } from '@/constants/paths'
import { ContentCollectionItem } from '@/services/CollectionsService/types'
import { isHasTypeName, LinkViewModel, PageDataContext } from '@/services/RenderService'
import { CollectionDisplay as CollectionDisplayType } from '@/types/codegen-contentful'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'

export function isContentCollection(content: unknown) {
  return isHasTypeName(content) && content.__typename === 'ContentCollection'
}

export function mapContentCollectionIdsToCollections(
  pageDataContext: PageDataContext,
  contentCollectionId?: string | null,
): ContentCollectionItem[] {
  if (!contentCollectionId) {
    logger().warn(`No contentCollectionId provided!`)
    return []
  }

  for (const collection of pageDataContext?.['content-collection'] || []) {
    if (collection?.id === contentCollectionId) {
      return (
        (collection?.connection?.edges?.map((edge) => edge?.node).filter(isDefined) as ContentCollectionItem[]) || []
      )
    }
  }

  logger().warn(`Expected to find a content collection from the "content-collection" by id, but found nothing!`, {
    contentCollectionId,
  })
  return []
}

export function mapContentCollectionsToViewModels(
  contentCollections: ContentCollectionItem[] | null,
  collectionDisplay: CollectionDisplayType,
): LinkViewModel[] {
  return (
    contentCollections
      ?.map((collectionItem: ContentCollectionItem) => {
        const projectType = returnProjectType(collectionItem)
        const isInEarlyAccess =
          collectionItem?.__typename === 'ContentSeries'
            ? collectionItem?.firstWatchable?.watchableAvailabilityStatus === 'ACTION_REQUIRED'
            : collectionItem?.watchableAvailabilityStatus === 'ACTION_REQUIRED'
        const linkUrl = buildLinkUrl(collectionItem)
        if (!linkUrl) {
          logger().warn(`No linkUrl found for collectionItem`, { collectionItem })
          return null
        }

        const landscapeUrl =
          collectionItem?.landscapeAngelImage?.cloudinaryPath ??
          collectionItem?.landscapeTitleImage?.cloudinaryPath ??
          collectionItem?.landscapeNonTitleImage?.cloudinaryPath ??
          collectionItem?.landscapeStillImage?.cloudinaryPath
        const landscapeNonTitleImage =
          collectionItem?.landscapeBackdropImage?.cloudinaryPath ??
          collectionItem?.landscapeNonTitleImage?.cloudinaryPath ??
          collectionItem?.landscapeStillImage?.cloudinaryPath
        const squareNonTitleImage =
          collectionItem?.posterNonTitleImage?.cloudinaryPath ?? collectionItem?.squareNonTitleImage?.cloudinaryPath
        const portraitUrl =
          collectionItem?.portraitAngelImage?.cloudinaryPath ??
          collectionItem?.posterTitleImage?.cloudinaryPath ??
          collectionItem?.posterNonTitleImage?.cloudinaryPath ??
          collectionItem?.posterStillImage?.cloudinaryPath
        const imageUrl = collectionDisplay?.displayAs?.toLowerCase().includes('portrait') ? portraitUrl : landscapeUrl

        return {
          id: collectionItem?.id,
          name: collectionItem?.name ?? undefined,
          imageUrl,
          landscapeUrl,
          portraitUrl,
          landscapeNonTitleImage,
          squareNonTitleImage,
          logoUrl: collectionItem?.logoImage?.cloudinaryPath,
          linkUrl: linkUrl,
          isInEarlyAccess: isInEarlyAccess,
          projectType: projectType,
          projectSlug: ('project' in collectionItem && collectionItem?.project?.slug) || undefined,
          releaseDate: ('project' in collectionItem && collectionItem?.project?.releaseDate) || undefined,
          description: collectionItem?.description,
          genres: 'genres' in collectionItem ? collectionItem?.genres : undefined,
          ratings: 'ratings' in collectionItem ? collectionItem?.ratings : undefined,
          highestScore: ('project' in collectionItem && collectionItem?.project?.highestScore) || undefined,
        }
      })
      ?.filter(isDefined) || []
  )
}

function isEpisode(content: ContentCollectionItem) {
  return content?.__typename === 'ContentEpisode'
}

function isSpecial(content: ContentCollectionItem) {
  return content?.__typename === 'ContentSpecial'
}

function isSeries(content: ContentCollectionItem) {
  return content?.__typename === 'ContentSeries'
}

function isSeason(content: ContentCollectionItem) {
  return content?.__typename === 'ContentSeason'
}

const returnProjectType = (content: ContentCollectionItem): 'special' | 'movie' | 'series' => {
  if (isSpecial(content)) return 'special'
  if (isEpisode(content) || isSeries(content)) return 'series'
  return 'movie'
}

const buildLinkUrl = (collectionItem: ContentCollectionItem) => {
  if (!('project' in collectionItem)) return null

  if (
    isSeason(collectionItem) &&
    'seasonNumber' in collectionItem &&
    collectionItem?.seasonNumber &&
    collectionItem?.project?.slug
  ) {
    return `${paths.watch.index}/${collectionItem?.project?.slug}?season=${collectionItem?.seasonNumber}`
  }

  if (isEpisode(collectionItem) || isSpecial(collectionItem)) {
    if (collectionItem?.id && collectionItem?.project?.slug) {
      return `${paths.watch.index}/${collectionItem?.project?.slug}/episode/${collectionItem?.id}`
    }
    if (collectionItem?.project?.slug) {
      logger().warn(`ContentCollection: No id found for collectionItem`, { collectionItem })
      return `${paths.watch.index}/${collectionItem?.project?.slug}`
    } else {
      logger().warn(`ContentCollection: No id and slug found for collectionItem`, { collectionItem })
      return paths.watch.index
    }
  } else {
    return `${paths.watch.index}/${collectionItem?.project?.slug}`
  }
}
